import { mergeChartTheme } from '../../core/ChartTheme';
import { createEChartsTooltipFormatter } from './EChartsTooltipFormatter';
export function convertToEChartsOption(config) {
    const { data, type, series, xAxis, yAxis, tooltip, legend, colors } = config;
    const theme = mergeChartTheme(config.theme);
    const xAxisData = data.map(item => {
        const key = xAxis?.dataKey ?? 'name';
        return item[key] ?? '';
    });
    const echartsSeries = series.map((serie, index) => {
        const serieData = data.map(item => {
            const value = item[serie.dataKey];
            return typeof value === 'number' ? value : 0;
        });
        const serieColor = serie.stroke ?? colors?.[index] ?? getDefaultColor(index);
        const serieType = serie.type ?? type;
        const showMarkers = theme.markers?.show !== false;
        const isAreaChart = type === 'area' || serieType === 'area';
        const echartsType = isAreaChart ? 'line' : serieType;
        const baseSerie = {
            name: serie.name,
            type: echartsType,
            data: serieData,
            itemStyle: {
                color: serieColor,
            },
        };
        if (serie.stack !== undefined)
            baseSerie.stack = serie.stack;
        if (serie.annotation) {
            baseSerie.markPoint = {
                symbol: 'triangle',
                symbolRotate: 180,
                symbolSize: 12,
                itemStyle: { color: serie.annotation.color },
                label: { show: true, position: 'top', formatter: serie.annotation.label, color: serie.annotation.color },
                data: [{ coord: [serie.annotation.x, serie.annotation.y] }],
            };
        }
        if (echartsType === 'line') {
            baseSerie.lineStyle = {
                width: 2,
                color: serieColor,
            };
            baseSerie.symbol = showMarkers ? 'circle' : 'none';
            baseSerie.symbolSize = showMarkers ? (theme.markers?.size ?? 6) : 0;
            if (isAreaChart) {
                baseSerie.areaStyle = {
                    color: serie.fill ?? serieColor,
                    opacity: 0.6,
                };
            }
        }
        else if (echartsType === 'bar') {
            baseSerie.itemStyle = {
                color: serie.fill ?? serieColor,
            };
        }
        else if (echartsType === 'scatter') {
            baseSerie.symbolSize = showMarkers ? (theme.markers?.size ?? 6) : 0;
        }
        return baseSerie;
    });
    const option = {
        backgroundColor: theme.backgroundColor,
        textStyle: {
            fontFamily: theme.fontFamily,
            color: theme.textColor,
        },
        xAxis: {
            type: 'category',
            data: xAxisData,
            name: xAxis?.label,
            axisLabel: {
                color: theme.axis?.x?.labelColor ?? theme.textColor,
                fontFamily: theme.axis?.x?.fontFamily ?? theme.fontFamily,
                fontSize: theme.axis?.x?.fontSize ?? 12,
                lineHeight: theme.axis?.x?.lineHeight,
                align: theme.axis?.x?.textAlign ?? 'center',
            },
            axisLine: {
                lineStyle: {
                    color: theme.axis?.x?.lineColor,
                },
            },
            axisTick: {
                lineStyle: {
                    color: theme.axis?.x?.tickColor ?? theme.axis?.x?.lineColor,
                },
            },
        },
        yAxis: {
            type: 'value',
            name: yAxis?.label,
            axisLabel: {
                color: theme.axis?.y?.labelColor ?? theme.textColor,
                fontFamily: theme.axis?.y?.fontFamily ?? theme.fontFamily,
                fontSize: theme.axis?.y?.fontSize ?? 12,
                lineHeight: theme.axis?.y?.lineHeight,
                align: theme.axis?.y?.textAlign ?? 'right',
                formatter: theme.yAxisFormatter,
            },
            axisLine: theme.axis?.y?.lineColor !== undefined
                ? {
                    show: true,
                    lineStyle: {
                        color: theme.axis.y.lineColor,
                    },
                }
                : {
                    show: false,
                },
            splitLine: {
                lineStyle: {
                    color: theme.grid?.stroke,
                    type: 'dashed',
                },
            },
        },
        grid: {
            left: theme.grid?.left ?? '3%',
            right: theme.grid?.right ?? '4%',
            top: theme.grid?.top ?? '3%',
            bottom: theme.grid?.bottom ?? '3%',
            containLabel: true,
        },
        series: echartsSeries,
        tooltip: tooltip !== false
            ? {
                trigger: 'axis',
                backgroundColor: theme.tooltip?.backgroundColor,
                borderColor: theme.tooltip?.borderColor,
                borderWidth: theme.tooltip?.borderWidth ?? 1,
                borderRadius: theme.tooltip?.borderRadius ?? 0,
                padding: theme.tooltip?.padding ?? [12, 16],
                textStyle: {
                    color: theme.tooltip?.textColor,
                    fontFamily: theme.fontFamily,
                    fontSize: theme.tooltip?.fontSize ?? 12,
                },
                formatter: theme.tooltip?.dataFormatter
                    ? (params) => {
                        const tooltipData = theme.tooltip?.dataFormatter?.(params);
                        if (tooltipData && 'items' in tooltipData && Array.isArray(tooltipData.items)) {
                            return createEChartsTooltipFormatter(tooltipData);
                        }
                        return '';
                    }
                    : undefined,
                extraCssText: `
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              gap: 8px;
              font-family: 'Source Sans 3', var(--font-family-base);
              filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.12));
            `,
            }
            : undefined,
        legend: legend !== false && theme.legend?.show !== false
            ? {
                data: series.map(s => s.name),
                textStyle: {
                    color: theme.legend?.textColor ?? theme.textColor,
                    fontFamily: theme.fontFamily,
                },
            }
            : undefined,
        color: colors,
    };
    return option;
}
function getDefaultColor(index) {
    const defaultColors = [
        'var(--data-series-01, #387C2B)',
        'var(--data-series-02, #1573D3)',
        'var(--data-series-03, #FEA601)',
        'var(--data-series-04, #DC0A0A)',
        'var(--data-series-05, #EE5005)',
        'var(--data-series-06, #750AC2)',
    ];
    return defaultColors[index % defaultColors.length];
}
